// Name Your Price Tool
// Allows users to set budget and quantity, then shows matching products from Shopify

class NameYourPriceTool {
  constructor() {
    this.quantitySlider = document.getElementById('quantity-slider');
    this.priceSlider = document.getElementById('price-slider');
    this.frontDecoSelect = document.getElementById('front-deco');
    this.backDecoSelect = document.getElementById('back-deco');
    this.quantityDisplay = document.getElementById('quantity-display');
    this.priceDisplay = document.getElementById('price-display');
    this.totalDisplay = document.getElementById('total-display');
    this.resultsContainer = document.getElementById('results-container');
    this.resultsCount = document.getElementById('results-count');
    this.loadingIndicator = document.getElementById('loading-indicator');
    this.noResults = document.getElementById('no-results');
    this.productTypeFilter = document.getElementById('product-type-filter');

    this.priceTolerance = 0.50; // Products within this range above target still show as "close match"

    // Load products from Shopify (rendered by Liquid)
    this.products = [];
    this.loadProductData();
    this.populateProductTypeFilter();

    this.init();
  }

  loadProductData() {
    try {
      const productDataElement = document.getElementById('nyp-product-data');
      if (!productDataElement) {
        console.error('Product data not found. Make sure the Liquid snippet is rendering correctly.');
        return;
      }

      const data = JSON.parse(productDataElement.textContent);

      // Map products from collection data
      this.products = data.products.filter(p => p.item_code).map(product => ({
        handle: product.handle,
        name: product.title,
        code: product.item_code,
        category: product.product_type,
        image: product.featured_image,
        url: product.url,
        available: product.available
      }));
    } catch (error) {
      console.error('Error loading product data:', error);
    }
  }

  populateProductTypeFilter() {
    if (!this.productTypeFilter) return;

    const types = [...new Set(this.products.map(p => p.category).filter(Boolean))].sort();
    types.forEach(type => {
      const option = document.createElement('option');
      option.value = type;
      option.textContent = type;
      this.productTypeFilter.appendChild(option);
    });
  }

  init() {
    if (!this.quantitySlider || !this.priceSlider || !this.frontDecoSelect || !this.backDecoSelect) {
      console.error('Name Your Price Tool: Missing required elements');
      return;
    }

    // Initialized

    // Bind event listeners
    this.quantitySlider.addEventListener('input', () => this.handleSliderChange());
    this.priceSlider.addEventListener('input', () => this.handleSliderChange());
    this.frontDecoSelect.addEventListener('change', () => this.handleDecorationChange());
    this.backDecoSelect.addEventListener('change', () => this.handleDecorationChange());
    if (this.productTypeFilter) {
      this.productTypeFilter.addEventListener('change', () => this.handleSliderChange());
    }

    this.presetChips = document.querySelectorAll('.nyp-preset-chip');
    this.presetChips.forEach(chip => {
      chip.addEventListener('click', () => this.applyPreset(chip));
    });

    // Disable selection of "None" for both (at least one must be selected)
    this.updateDecorationOptions();

    // Initial calculation
    this.handleSliderChange();
  }

  applyPreset(chip) {
    const { qty, price, front, back } = chip.dataset;

    if (qty) this.quantitySlider.value = qty;
    if (price) this.priceSlider.value = price;
    if (front !== undefined) this.frontDecoSelect.value = front;
    if (back !== undefined) this.backDecoSelect.value = back;

    this.updateDecorationOptions();
    this.handleSliderChange();
  }

  updateDecorationOptions() {
    const frontValue = this.frontDecoSelect.value;
    const backValue = this.backDecoSelect.value;

    // Disable "None" option if the other side is also "None"
    if (backValue === "0") {
      this.frontDecoSelect.querySelector('option[value="0"]').disabled = true;
    } else {
      this.frontDecoSelect.querySelector('option[value="0"]').disabled = false;
    }

    if (frontValue === "0") {
      this.backDecoSelect.querySelector('option[value="0"]').disabled = true;
    } else {
      this.backDecoSelect.querySelector('option[value="0"]').disabled = false;
    }
  }

  handleDecorationChange() {
    this.updateDecorationOptions();
    this.handleSliderChange(); // Recalculate with new decoration options
  }

  handleSliderChange() {
    const quantity = parseInt(this.quantitySlider.value);
    const targetPrice = parseFloat(this.priceSlider.value);

    // Update displays
    this.quantityDisplay.textContent = quantity.toLocaleString();
    this.priceDisplay.textContent = '$' + targetPrice.toFixed(2);
    this.totalDisplay.textContent = '$' + (quantity * targetPrice).toFixed(2);

    // Debounce the search
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.findMatchingProducts(quantity, targetPrice);
    }, 300);
  }

  findMatchingProducts(quantity, targetPrice) {
    this.showLoading(true);

    const matches = [];
    const frontColors = this.frontDecoSelect.value;
    const backColors = this.backDecoSelect.value;

    // Create decoration description
    const decorationName = this.getDecorationName(frontColors, backColors);

    // Apply product type filter
    const selectedType = this.productTypeFilter ? this.productTypeFilter.value : '';

    // Test each product with the selected decoration
    this.products.forEach(product => {
      if (!product.available) return;
      if (selectedType && product.category !== selectedType) return;

      const calculatedPrice = this.calculatePrice(product, quantity, frontColors, backColors);
      if (!calculatedPrice) return;

      // Show all products at or below the target price,
      // plus anything just slightly above (within tolerance)
      if (calculatedPrice <= targetPrice + this.priceTolerance) {
        matches.push({
          product: product,
          decorationName: decorationName,
          frontColors: frontColors,
          backColors: backColors,
          calculatedPrice: calculatedPrice,
          quantity: quantity,
          total: calculatedPrice * quantity,
          difference: calculatedPrice - targetPrice
        });
      }
    });

    // Sort: closest to budget first (most expensive within budget at top)
    matches.sort((a, b) => Math.abs(a.difference) - Math.abs(b.difference));

    // Display results
    this.displayResults(matches);
  }

  getDecorationName(frontColors, backColors) {
    const frontNum = parseInt(frontColors);
    const backNum = parseInt(backColors);

    if (frontNum === 0 && backNum === 0) {
      return "No decoration";
    }

    const parts = [];
    if (frontNum > 0) {
      parts.push(`Front: ${frontNum} color${frontNum > 1 ? 's' : ''}`);
    }
    if (backNum > 0) {
      parts.push(`Back: ${backNum} color${backNum > 1 ? 's' : ''}`);
    }

    return parts.join(' • ');
  }

  /**
   * The customer price per piece — the pricing engine's calculateOrderPrice
   * (ops.goodink.com/api/quote/pricing.js), the one function every Good Ink
   * tool prices with. This tool only builds the spec: a screen print of the
   * chosen inks per side at 50 pieces and up; under 50 the shop prints a
   * full-color 8" x 8" transfer instead (the engine refuses a screen print
   * under 50 and a full-color print under 10 — null then, the card is skipped).
   */
  calculatePrice(product, quantity, frontColors, backColors) {
    try {
      const itemCode = product.code;
      if (typeof getItemPrice(itemCode) !== 'number') return null; // not in the product table (getItemPrice is null for an unknown code)

      const fullColor = quantity < 50;
      const location = (id, inks) => fullColor
        ? { id, method: 'Full Color Print', sizeBucket: 'UCM_STANDARD' }
        : { id, method: 'Printing', inks: parseInt(inks, 10) };
      const locations = [];
      if (frontColors !== "0") locations.push(location('front', frontColors));
      if (backColors !== "0") locations.push(location('back', backColors));

      const result = calculateOrderPrice({
        decorations: [{ id: 'nyp', method: locations.length ? locations[0].method : 'Printing', locations, products: [{ itemCode, quantity }] }],
        applyConditionalCharges: true,
        includeStoreCharge: false
      });
      const line = result.decorations[0].products[0];
      return typeof line.unitPrice === 'number' ? line.unitPrice : null;
    } catch (error) {
      console.error('Error calculating price for', product.code, error);
      return null;
    }
  }

  displayResults(matches) {
    this.showLoading(false);
    this.resultsCount.textContent = `(${matches.length})`;

    if (matches.length === 0) {
      this.resultsContainer.style.display = 'none';
      this.noResults.style.display = 'flex';
      return;
    }

    this.noResults.style.display = 'none';
    this.resultsContainer.style.display = 'grid';
    this.resultsContainer.innerHTML = '';

    // Stagger card entrance
    matches.forEach((match, i) => {
      const card = this.createResultCard(match);
      card.style.animationDelay = `${i * 0.04}s`;
      this.resultsContainer.appendChild(card);
    });
  }

  createResultCard(match) {
    const card = document.createElement('div');
    card.className = 'nyp-result-card';

    const absDiff = Math.abs(match.difference);
    const isPerfectMatch = absDiff < 0.01;
    const isCloseMatch = absDiff < 0.50 && !isPerfectMatch;

    card.innerHTML = `
      ${match.product.image ? `
        <div class="nyp-card-image">
          <img src="${match.product.image}" alt="${match.product.name}" loading="lazy" />
        </div>
      ` : ''}

      <div class="nyp-card-body">
        <div class="nyp-card-header">
          <h3 class="nyp-card-title">${match.product.name}</h3>
          ${isPerfectMatch ? '<span class="nyp-badge nyp-badge-perfect">Perfect Match</span>' : ''}
          ${isCloseMatch ? '<span class="nyp-badge nyp-badge-exact">Close Match</span>' : ''}
        </div>

        <div class="nyp-card-pricing">
          <div class="nyp-card-price-row">
            <span>Price per item:</span>
            <strong>$${match.calculatedPrice.toFixed(2)}</strong>
          </div>
          <div class="nyp-card-price-row">
            <span>Total (${match.quantity.toLocaleString()} items):</span>
            <strong>$${match.total.toFixed(2)}</strong>
          </div>
          ${absDiff > 0.01 ? `
            <div class="nyp-card-difference ${match.difference > 0 ? 'nyp-card-difference--over' : 'nyp-card-difference--under'}">
              ${match.difference > 0 ? '$' + absDiff.toFixed(2) + ' above your budget' : '$' + absDiff.toFixed(2) + ' below budget!'}
            </div>
          ` : ''}
        </div>

        <div class="nyp-card-actions">
          <a href="/pages/request-a-quote?item=${encodeURIComponent(match.product.name)}&itemCode=${match.product.code}&qty=${match.quantity}&frontDeco=${match.frontColors}&backDeco=${match.backColors}&decoration=${encodeURIComponent(match.decorationName)}&totalPerItem=${match.calculatedPrice.toFixed(2)}&total=${match.total.toFixed(2)}&referrer=name-your-price"
             class="nyp-btn nyp-btn-primary">
            Continue with a Quote
          </a>
          <a href="${match.product.url}" class="nyp-btn nyp-btn-secondary">
            View Product
          </a>
        </div>
      </div>
    `;

    return card;
  }

  showLoading(show) {
    if (show) {
      this.loadingIndicator.style.display = 'flex';
      this.resultsContainer.style.display = 'none';
      this.noResults.style.display = 'none';
    } else {
      this.loadingIndicator.style.display = 'none';
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if we're on a page with the name-your-price tool
  if (document.getElementById('name-your-price-tool')) {
    new NameYourPriceTool();
  }
});
